import { __assign, __awaiter, __generator, __read, __rest } from "tslib";
import React, { useEffect, useState, useRef } from "react";
import ReactDOM from "react-dom";
import { Modal } from "./ModalMain";
import { Button } from "../button";
import { ModalMessage } from "./ModalMessage";
import { useTranslation } from "../i18n";
import { ConfigProvider } from "../configprovider";
import { noop } from "../_util/noop";
/**
 * API 的方式唤起一个确认对话框
 * @param options
 * @returns 异步返回布尔值，为 true 则表示用户确认，为 false 则表示用户取消
 */
export function confirm(options) {
    var _this = this;
    var _a = options.onOk, onOk = _a === void 0 ? noop : _a, _b = options.onCancel, onCancel = _b === void 0 ? noop : _b, _c = options.destoryOnRoute, destoryOnRoute = _c === void 0 ? true : _c, restOptions = __rest(options, ["onOk", "onCancel", "destoryOnRoute"]);
    return new Promise(function (resolve) {
        var el = document.createElement("div");
        // 创建销毁函数，用于真正销毁组件和DOM，同时处理Promise
        var destroyInstance = function () {
            if (destoryOnRoute) {
                resolve(false); // URL变化时默认为取消
                ReactDOM.unmountComponentAtNode(el);
                if (el.parentNode) {
                    el.parentNode.removeChild(el);
                }
            }
        };
        ReactDOM.render(React.createElement(ConfigProvider, null,
            React.createElement(ModalConfirmAPI, __assign({}, restOptions, { onOk: function () { return __awaiter(_this, void 0, void 0, function () {
                    return __generator(this, function (_a) {
                        switch (_a.label) {
                            case 0: return [4 /*yield*/, onOk()];
                            case 1:
                                _a.sent();
                                resolve(true);
                                return [2 /*return*/];
                        }
                    });
                }); }, onCancel: function () { return __awaiter(_this, void 0, void 0, function () {
                    return __generator(this, function (_a) {
                        switch (_a.label) {
                            case 0: return [4 /*yield*/, onCancel()];
                            case 1:
                                _a.sent();
                                resolve(false);
                                return [2 /*return*/];
                        }
                    });
                }); }, onExited: function () {
                    ReactDOM.unmountComponentAtNode(el);
                    if (el.parentNode) {
                        el.parentNode.removeChild(el);
                    }
                }, __onExited: destroyInstance }))), el);
    });
}
function ModalConfirmAPI(_a) {
    var _this = this;
    var onOk = _a.onOk, onCancel = _a.onCancel, __onExited = _a.__onExited, confirmModalProps = __rest(_a, ["onOk", "onCancel", "__onExited"]);
    var _b = __read(useState(false), 2), visible = _b[0], setVisible = _b[1];
    // 渲染之后，马上显示
    useEffect(function () { return setVisible(true); }, []);
    // 监听URL变化，直接调用onExited
    useEffect(function () {
        var handleLocationChange = function () {
            if (__onExited) {
                __onExited();
            }
        };
        // 监听popstate事件（浏览器前进后退）
        window.addEventListener("popstate", handleLocationChange);
        // 监听hashchange事件（hash路由变化）
        window.addEventListener("hashchange", handleLocationChange);
        return function () {
            window.removeEventListener("popstate", handleLocationChange);
            window.removeEventListener("hashchange", handleLocationChange);
        };
    }, [__onExited]);
    var ok = function () { return __awaiter(_this, void 0, void 0, function () {
        return __generator(this, function (_a) {
            switch (_a.label) {
                case 0: return [4 /*yield*/, onOk()];
                case 1:
                    _a.sent();
                    setVisible(false);
                    return [2 /*return*/];
            }
        });
    }); };
    var cancel = function () { return __awaiter(_this, void 0, void 0, function () {
        return __generator(this, function (_a) {
            switch (_a.label) {
                case 0: return [4 /*yield*/, onCancel()];
                case 1:
                    _a.sent();
                    setVisible(false);
                    return [2 /*return*/];
            }
        });
    }); };
    return (React.createElement(ModalConfirm, __assign({ visible: visible, onOk: ok, onCancel: cancel, onClose: cancel }, confirmModalProps)));
}
/**
 * 提供原始的 ModalConfirm 组件。
 * 推荐使用 `Modal.confirm()` API 来简化用法
 */
export function ModalConfirm(_a) {
    var message = _a.message, description = _a.description, icon = _a.icon, modalProps = __rest(_a, ["message", "description", "icon"]);
    useEffect(function () {
        window.focus();
    }, []);
    return (React.createElement(Modal, __assign({ disableEscape: true }, modalProps),
        React.createElement(Modal.Body, null,
            React.createElement(ModalMessage, { icon: icon, message: message, description: description })),
        React.createElement(Modal.Footer, null,
            React.createElement(ModalButton, __assign({}, modalProps)))));
}
function ModalButton(_a) {
    var _this = this;
    var onOk = _a.onOk, okText = _a.okText, _b = _a.okType, okType = _b === void 0 ? "primary" : _b, onCancel = _a.onCancel, cancelText = _a.cancelText, _c = _a.cancelType, cancelType = _c === void 0 ? "weak" : _c;
    var t = useTranslation();
    var mainButtonRef = useRef(null);
    var _d = __read(useState(false), 2), okLoading = _d[0], setOkLoading = _d[1];
    var _e = __read(useState(false), 2), cancelLoading = _e[0], setCancelLoading = _e[1];
    useEffect(function () {
        if (mainButtonRef.current) {
            mainButtonRef.current.focus();
        }
    }, []);
    return (React.createElement(React.Fragment, null,
        React.createElement(Button, { type: okType, htmlType: "button", loading: okLoading, disabled: cancelLoading, onClick: function () { return __awaiter(_this, void 0, void 0, function () {
                return __generator(this, function (_a) {
                    switch (_a.label) {
                        case 0:
                            setOkLoading(true);
                            return [4 /*yield*/, onOk()];
                        case 1:
                            _a.sent();
                            setOkLoading(false);
                            return [2 /*return*/];
                    }
                });
            }); }, ref: mainButtonRef }, okText || t.okText),
        React.createElement(Button, { type: cancelType, htmlType: "button", loading: cancelLoading, disabled: okLoading, onClick: function () { return __awaiter(_this, void 0, void 0, function () {
                return __generator(this, function (_a) {
                    switch (_a.label) {
                        case 0:
                            setCancelLoading(true);
                            return [4 /*yield*/, onCancel()];
                        case 1:
                            _a.sent();
                            setCancelLoading(false);
                            return [2 /*return*/];
                    }
                });
            }); } }, cancelText || t.cancelText)));
}
//# sourceMappingURL=ModalConfirm.js.map